import dayjs from "dayjs";
import firebase from "../../configs/FirebaseConfig";
import {
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
} from "../../constants/FirestoreConstant";
import {
  computeFinalInvoiceTotals,
  describeProformaLinkConflict,
  planProformaLinkChange,
  sumProformaPaid,
  totalsDiffer,
} from "../proformaLinks";

const firestore = firebase.firestore();
const invoicesCol = () => firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE);

/**
 * Schrijfkant van de koppeling pro forma <-> definitieve factuur. Wat er geschreven wordt, bepaalt
 * planProformaLinkChange (../proformaLinks.js); hier gebeurt het in één transactie, met documenten
 * die in die transactie opnieuw zijn gelezen. Zo kan een verouderd scherm (of een collega die
 * tegelijk koppelt) nooit een pro forma aan twee facturen hangen of andermans koppeling wissen.
 */

export class ProformaLinkConflictError extends Error {
  constructor(conflicts, messages) {
    super(messages.join(" "));
    this.name = "ProformaLinkConflictError";
    this.conflicts = conflicts;
    this.messages = messages;
  }
}

const paymentsCol = () => firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE);

// Som van de eigen betalingen per factuur-id. Een query kan niet in een transactie, dus dit
// gebeurt vooraf. Betalingen veranderen niet door koppelen.
export const sumOwnPayments = async (invoiceIds) => {
  const entries = await Promise.all(
    [...new Set(invoiceIds)].map(async (id) => {
      const snap = await paymentsCol().where("invoiceId", "==", id).get();
      return [id, snap.docs.reduce((sum, d) => sum + (d.data().amount || 0), 0)];
    }),
  );
  return new Map(entries);
};

// Herberekent totalPaid/amountDue/status van een definitieve factuur uit zijn eigen betalingen
// plus de (al bijgehouden) totalPaid van elke gekoppelde pro forma. Maakt nooit een betaling aan.
//
// Alleen gebruiken na GEWONE writes (betaling toevoegen/wijzigen/verwijderen). Na een transactie
// niet: get() wordt beantwoord door een listener die al op hetzelfde document staat (de
// detailpagina heeft er een), ook met { source: "server" }, en die ziet de writes van een
// transactie pas als de server ze terugstuurt. Dan lees je de factuur van vóór de transactie.
// Koppelen en verwijderen berekenen de totalen daarom in de transactie zelf.
export const recomputeFinalInvoiceTotals = async (finalInvoiceId, email) => {
  const finalRef = invoicesCol().doc(finalInvoiceId);
  const finalSnap = await finalRef.get();
  if (!finalSnap.exists) return;
  const finalData = finalSnap.data();

  const ownPaid = (await sumOwnPayments([finalInvoiceId])).get(finalInvoiceId);
  const proformaSnaps = await Promise.all(
    (finalData.linkedProformaInvoiceIds || []).map((id) => invoicesCol().doc(id).get()),
  );
  const proformasById = new Map(proformaSnaps.filter((s) => s.exists).map((s) => [s.id, s.data()]));
  const totals = computeFinalInvoiceTotals({
    totalAmount: finalData.totalAmount || 0,
    ownPaid,
    linkedPaid: sumProformaPaid(finalData.linkedProformaInvoiceIds, proformasById),
  });

  await finalRef.update({ ...totals, updatedAt: dayjs().unix(), updatedBy: email });
};

/**
 * Zet de gekoppelde pro forma's van een definitieve factuur op `desiredIds`.
 * Gooit ProformaLinkConflictError (zonder iets te schrijven) als een koppeling niet kan.
 * @returns {Promise<{changed: boolean, plan?: object}>}
 */
export const saveProformaLinks = async ({ finalInvoiceId, desiredIds, email, invoicesById }) => {
  const finalRef = invoicesCol().doc(finalInvoiceId);
  const before = await finalRef.get();
  if (!before.exists) throw new Error("Definitieve factuur bestaat niet meer");
  const { supplierId } = before.data();

  // Andere facturen van deze leverancier die een van de betrokken pro forma's claimen. Een query
  // kan niet binnen een transactie; de gevonden documenten worden daarin wel opnieuw gelezen.
  const involved = new Set([...(before.data().linkedProformaInvoiceIds || []), ...desiredIds]);
  // Zonder supplierId (zeer oude facturen) kan er niets gekoppeld worden: de planner weigert
  // dan elke pro forma als "andere leverancier".
  const supplierInvoices = supplierId ? await invoicesCol().where("supplierId", "==", supplierId).get() : { docs: [] };
  const otherFinalIds = supplierInvoices.docs
    .filter((d) => d.id !== finalInvoiceId && d.data().type === "final")
    .filter((d) => (d.data().linkedProformaInvoiceIds || []).some((id) => involved.has(id)))
    .map((d) => d.id);
  const ownPaidById = await sumOwnPayments([finalInvoiceId, ...otherFinalIds]);

  let result = { changed: false };
  await firestore.runTransaction(async (tx) => {
    // In een transactie eerst alle reads, dan pas writes.
    const finalSnap = await tx.get(finalRef);
    if (!finalSnap.exists) throw new Error("Definitieve factuur bestaat niet meer");
    const finalInvoice = { id: finalSnap.id, ...finalSnap.data() };
    const proformaIds = [...new Set([...(finalInvoice.linkedProformaInvoiceIds || []), ...desiredIds])];
    const proformaSnaps = await Promise.all(proformaIds.map((id) => tx.get(invoicesCol().doc(id))));
    const otherSnaps = await Promise.all(otherFinalIds.map((id) => tx.get(invoicesCol().doc(id))));

    const proformasById = new Map(proformaSnaps.filter((s) => s.exists).map((s) => [s.id, { id: s.id, ...s.data() }]));
    const otherFinals = otherSnaps.filter((s) => s.exists).map((s) => ({ id: s.id, ...s.data() }));
    const plan = planProformaLinkChange({ finalInvoice, desiredIds, proformasById, otherFinals });

    if (plan.conflicts.length > 0) {
      const lookup = new Map(invoicesById || []);
      otherFinals.forEach((f) => lookup.set(f.id, f));
      throw new ProformaLinkConflictError(
        plan.conflicts,
        plan.conflicts.map((c) => describeProformaLinkConflict(c, lookup)),
      );
    }

    // Totalen hier, in de transactie, uit de pro forma's die we net gelezen hebben. Niet achteraf
    // terug-lezen: zie recomputeFinalInvoiceTotals.
    // Pro forma's die bij een losgelaten factuur blijven hangen, hebben we nog niet gelezen.
    const missingIds = [
      ...new Set(plan.releasedFrom.flatMap((f) => f.linkedProformaInvoiceIds).filter((id) => !proformasById.has(id))),
    ];
    const missingSnaps = await Promise.all(missingIds.map((id) => tx.get(invoicesCol().doc(id))));
    missingSnaps.filter((s) => s.exists).forEach((s) => proformasById.set(s.id, { id: s.id, ...s.data() }));

    const totalsFor = (invoice, linkedIds) =>
      computeFinalInvoiceTotals({
        totalAmount: invoice.totalAmount || 0,
        ownPaid: ownPaidById.get(invoice.id) || 0,
        linkedPaid: sumProformaPaid(linkedIds, proformasById),
      });
    const finalTotals = totalsFor(finalInvoice, plan.linkedIds);
    const now = dayjs().unix();

    if (!plan.changed) {
      // Niets aan de koppeling veranderd, maar opslaan herstelt wel verouderde totalen.
      if (totalsDiffer(finalInvoice, finalTotals)) {
        tx.update(finalRef, { ...finalTotals, updatedAt: now, updatedBy: email });
      }
      result = { changed: false, plan, totalsRepaired: totalsDiffer(finalInvoice, finalTotals) };
      return;
    }

    const numberOf = (id) => proformasById.get(id)?.invoiceNumber || id;
    const finalNumber = finalInvoice.invoiceNumber || finalInvoice.id;
    const history = (ref, changes) =>
      tx.set(ref.collection("history").doc(), { changedBy: email, changedAt: now, changes });

    tx.update(finalRef, {
      linkedProformaInvoiceIds: plan.linkedIds.length > 0 ? plan.linkedIds : null,
      ...finalTotals,
      updatedAt: now,
      updatedBy: email,
    });

    plan.proformaUpdates.forEach(({ id, linkedFinalInvoiceId }) => {
      const ref = invoicesCol().doc(id);
      tx.update(ref, {
        linkedFinalInvoiceId,
        linkedAt: linkedFinalInvoiceId ? now : null,
        updatedAt: now,
        updatedBy: email,
      });
      const restored = plan.restored.includes(id);
      history(ref, [
        linkedFinalInvoiceId
          ? {
              field: "proforma_linked",
              oldValue: null,
              newValue: finalInvoice.id,
              action: "invoice_edit",
              metadata: restored
                ? `+ Koppeling hersteld met definitieve factuur: ${finalNumber}`
                : `+ Gekoppeld aan definitieve factuur: ${finalNumber}`,
            }
          : {
              field: "proforma_unlinked",
              oldValue: finalInvoice.id,
              newValue: null,
              action: "invoice_edit",
              metadata: `- Ontkoppeld van definitieve factuur: ${finalNumber}`,
            },
      ]);
    });

    plan.releasedFrom.forEach((other) => {
      const ref = invoicesCol().doc(other.id);
      const otherInvoice = otherFinals.find((f) => f.id === other.id);
      tx.update(ref, {
        linkedProformaInvoiceIds: other.linkedProformaInvoiceIds.length > 0 ? other.linkedProformaInvoiceIds : null,
        ...totalsFor(otherInvoice, other.linkedProformaInvoiceIds),
        updatedAt: now,
        updatedBy: email,
      });
      history(
        ref,
        other.releasedIds.map((pid) => ({
          field: "proforma_unlinked",
          oldValue: pid,
          newValue: null,
          action: "invoice_edit",
          metadata: `- Proforma ontkoppeld: ${numberOf(pid)} (is verrekend met ${finalNumber})`,
        })),
      );
    });

    const changes = [
      ...plan.added.map((pid) => ({
        field: "proforma_linked",
        oldValue: null,
        newValue: pid,
        action: "invoice_edit",
        metadata: `+ Proforma gekoppeld: ${numberOf(pid)}`,
      })),
      ...plan.restored.map((pid) => ({
        field: "proforma_linked",
        oldValue: null,
        newValue: pid,
        action: "invoice_edit",
        metadata: `+ Koppeling hersteld: ${numberOf(pid)}`,
      })),
      ...plan.removed.map((pid) => ({
        field: "proforma_unlinked",
        oldValue: pid,
        newValue: null,
        action: "invoice_edit",
        metadata: `- Proforma ontkoppeld: ${numberOf(pid)}`,
      })),
    ];
    if (changes.length > 0) history(finalRef, changes);

    result = { changed: true, plan };
  });

  return result;
};
